// Plain-text views of a review finding for the review panel: the sentence
// around its anchor, the word diff of its fix, the one-line preview of a
// collapsed row, and an edited fix mapped back into the fix's HTML.
import { wordDiff } from './fixDiff';

const CONTEXT_CHARS = 90;

// Tags that stay inside a line of text; any other tag starts a new block
// (the same split the backend's BodyText makes).
const INLINE_TAGS = new Set(['a', 'b', 'code', 'em', 'i', 'mark', 's', 'span', 'strong', 'sub', 'sup', 'u']);
const TAG_RE = /<\/?([a-zA-Z][\w-]*)[^>]*>/g;
const SENTENCE_END = /[.!?]["'”’)\]]*(?=\s|$)/;
const LAST_SENTENCE_BREAK = /^[\s\S]*[.!?]["'”’)\]]*\s+/;

// The text a reader sees in an HTML fragment, whitespace collapsed but not
// trimmed. DOMParser never runs scripts or loads images.
export function htmlText(html) {
    if (!html) return '';
    // Parsed inside a wrapper: a document drops a fragment's leading spaces.
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    return (doc.body.firstChild?.textContent || '').replace(/\s+/g, ' ');
}

export const plainText = html => htmlText(html).trim();

const isBlockTag = name => !INLINE_TAGS.has(name.toLowerCase());

// Where the block holding html[index] starts: just after the last block
// tag before it.
function blockStart(html, index) {
    let start = 0;
    for (const match of html.slice(0, index).matchAll(TAG_RE)) {
        if (isBlockTag(match[1])) start = match.index + match[0].length;
    }
    return start;
}

// Where the block holding html[index] ends: at the next block tag.
function blockEnd(html, index) {
    for (const match of html.slice(index).matchAll(TAG_RE)) {
        if (isBlockTag(match[1])) return index + match.index;
    }
    return html.length;
}

function sentenceTail(text) {
    const match = text.match(LAST_SENTENCE_BREAK);
    return (match ? text.slice(match[0].length) : text).replace(/^\s+/, '');
}

function sentenceHead(text) {
    const match = text.match(SENTENCE_END);
    return (match ? text.slice(0, match.index + match[0].length) : text).replace(/\s+$/, '');
}

function clipStart(text) {
    if (text.length <= CONTEXT_CHARS) return text;
    const cut = text.slice(text.length - CONTEXT_CHARS);
    return `…${cut.slice(cut.indexOf(' ') + 1)}`;
}

function clipEnd(text) {
    if (text.length <= CONTEXT_CHARS) return text;
    const cut = text.slice(0, CONTEXT_CHARS);
    return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
}

// The rest of the sentence around `anchorHtml` in `html`: { pre, post },
// each cut to about 90 characters. Null when the anchor is not in html.
export function sentenceContext(html, anchorHtml) {
    const index = html && anchorHtml ? html.indexOf(anchorHtml) : -1;
    if (index === -1) return null;
    const end = index + anchorHtml.length;
    const pre = htmlText(html.slice(blockStart(html, index), index));
    const post = htmlText(html.slice(end, blockEnd(html, end)));
    return { pre: clipStart(sentenceTail(pre)), post: clipEnd(sentenceHead(post)) };
}

// The fix as word-diff parts ({ type: 'same' | 'removed' | 'added', text }).
// An addition shows only the words it inserts.
export function fixParts(anchorText, fixText, { addition = false } = {}) {
    const segments = wordDiff(anchorText, fixText);
    if (!addition) return segments;
    const inserted = segments.filter(seg => seg.type === 'added').map(seg => seg.text.trim()).join(' ');
    return [{ type: 'added', text: inserted || fixText }];
}

// A hand-edited fix: the whole anchor struck through, the edit added.
export const editedParts = (anchorText, editedText) => [
    { type: 'removed', text: anchorText },
    { type: 'added', text: editedText },
];

// One-line preview: long unchanged context at either end is cut to the two
// words nearest the change, keeping the spacing at the change.
export function shortParts(parts) {
    const last = parts.length - 1;
    return parts.map((part, i) => {
        if (part.type !== 'same' || last === 0 || part.text.trim().split(/\s+/).length <= 3) return part;
        if (i === 0) return { ...part, text: `…${part.text.match(/\S+\s+\S+\s*$/)[0]}` };
        if (i === last) return { ...part, text: `${part.text.match(/^\s*\S+\s+\S+/)[0]}…` };
        return part;
    });
}

// Each character a reader sees in `html` with its raw span; whitespace
// runs fold into one space and the ends are trimmed.
function readerChars(html) {
    const chars = [];
    const parser = new DOMParser();
    for (const match of html.matchAll(/<[^>]*>|&[#\w]+;|[\s\S]/g)) {
        const raw = match[0];
        if (raw.startsWith('<') && raw.length > 1) continue;
        let char = raw;
        if (raw.length > 1) char = parser.parseFromString(raw, 'text/html').body.textContent || raw;
        const start = match.index;
        const end = start + raw.length;
        const prev = chars[chars.length - 1];
        if (/\s/.test(char)) {
            if (prev && prev.char === ' ') prev.end = end;
            else if (prev) chars.push({ char: ' ', start, end });
            continue;
        }
        chars.push({ char, start, end });
    }
    while (chars.length && chars[chars.length - 1].char === ' ') chars.pop();
    return chars;
}

const escapeText = text => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// `fixHtml` with its reader text changed to `editedText`, keeping the
// markup around the change. Null when the change would cut through markup
// (say, half of a bold word), which the editor makes by hand instead.
export function editedFixHtml(fixHtml, editedText) {
    const chars = readerChars(fixHtml || '');
    const text = chars.map(c => c.char).join('');
    const edited = editedText.replace(/\s+/g, ' ').trim();
    if (edited === text) return fixHtml;
    let prefix = 0;
    const limit = Math.min(text.length, edited.length);
    while (prefix < limit && text[prefix] === edited[prefix]) prefix += 1;
    let suffix = 0;
    while (suffix < limit - prefix && text[text.length - 1 - suffix] === edited[edited.length - 1 - suffix]) suffix += 1;
    const changeStart = prefix;
    const changeEnd = text.length - suffix;
    let rawStart;
    let rawEnd;
    if (changeStart < changeEnd) {
        rawStart = chars[changeStart].start;
        rawEnd = chars[changeEnd - 1].end;
    } else {
        rawStart = changeStart < chars.length ? chars[changeStart].start : (chars.length ? chars[chars.length - 1].end : fixHtml.length);
        rawEnd = rawStart;
    }
    if (fixHtml.slice(rawStart, rawEnd).includes('<')) return null;
    return fixHtml.slice(0, rawStart) + escapeText(edited.slice(prefix, edited.length - suffix)) + fixHtml.slice(rawEnd);
}

// An addition's fix after a hand edit: its anchor sentence, then the
// edited sentence.
export const additionHtml = (anchorHtml, text) => `${anchorHtml} ${escapeText(text.replace(/\s+/g, ' ').trim())}`;
